// @flow

import * as React from 'react';
import {useState, useEffect, useContext} from 'react';
import type {Node} from 'react';
import {usePrefs, useAnalytics, useLocation} from 'docs/data';
import {useFormatPriceAmount} from 'docs/hooks';
import {useContent} from './ContentProvider';
import {useData} from './DataProvider';
import {getDefaultLanguage, getActiveDemo} from '../util';
import type {
  Demo,
  Demos,
  FileOptions,
  Languages,
  ActiveLanguages,
  Step,
} from './GuideTypes';

type GuideContextProps = {
  languages: Languages,
  merchant: boolean,
  keys: {publishable: string, secret: string},
  activeStep: ?Step,
  activeFile: string,
  activeDemo: ?Demo,
  options: ?FileOptions,
  setFile: (selectedFile: string) => () => void,
  changeSetting: (activeLanguages: ActiveLanguages) => void,
  scrollToStep: (stepId: string) => void,
  setActiveStepById: (stepId: string) => void,
  copyValue: (value: string) => boolean,
  toggleExtension: (id: string) => void,
  updateDemo: (updatedDemo: Demo) => void,
  setTab: (step: Step, tabId: string, file: string) => void,
  fireEvent: (action: string, data: {[key: string]: string}) => void,
};

const stubFunction = () => {
  throw new Error(
    'GuideContext function was called outside of GuideProvider scope.',
  );
};

const GuideContext = React.createContext<GuideContextProps>({
  languages: {allowed: {}, active: {}},
  merchant: true,
  keys: {publishable: '', secret: ''},
  activeStep: null,
  activeFile: '',
  activeDemo: null,
  options: null,
  setFile: stubFunction,
  changeSetting: stubFunction,
  scrollToStep: stubFunction,
  setActiveStepById: stubFunction,
  copyValue: stubFunction,
  toggleExtension: stubFunction,
  updateDemo: stubFunction,
  setTab: stubFunction,
  fireEvent: stubFunction,
});

type GuideProviderProps = {|
  keys: {secret: string, publishable: string},
  merchant: boolean,
  languages: Languages,
  demos: ?Demos,
  children: Node,
  containerRef: React.ElementRef<any> | null,
  siteCountry: string,
|};

// TODO(dopro) find some way to natively allow for this config override
function useLanguageOverrides(languages) {
  return React.useMemo(
    () =>
      languages.active.client === 'next'
        ? {
            ...languages,
            allowed: {
              ...languages.allowed,
              server: ['nextServer'],
            },
            active: {
              ...languages.active,
              server: 'nextServer',
            },
          }
        : languages,
    [languages],
  );
}

export const GuideProvider = (props: GuideProviderProps) => {
  const {getPref, setPrefs} = usePrefs();
  const {trackAction} = useAnalytics();
  const {activePrice, taxActivationStatus} = useData();
  const formatPriceAmount = useFormatPriceAmount();
  const {location, replaceLocation} = useLocation();

  const languagePrefs = {
    platform: getPref('platform'),
    client: getPref('client'),
    server: getPref('server') || getPref('lang'),
    reader: getPref('reader'),
    country: getPref('integration-country'),
  };

  const defaultLanguages = getDefaultLanguage(
    props.languages,
    languagePrefs,
    props.siteCountry,
  );
  const [langs, setLanguages] = useState(defaultLanguages);
  const languages = useLanguageOverrides(langs);

  const [activeFile, setActiveFile] = useState('');
  const [activeStep, setActiveStep] = useState<?Step>(null);

  const demo = getActiveDemo({
    demos: props.demos,
    languages: defaultLanguages,
  });
  const [demos, setDemos] = useState(props.demos);
  const [activeDemo, setActiveDemo] = useState(demo);

  const [prevScrollTop, setPrevScrollTop] = useState(window.pageYOffset);

  const {
    buildFiles,
    build,
    getIntegrationFiles,
    getStep,
    updateExtension,
    updateStep,
    steps,
  } = useContent();

  const buildRef = React.useRef(build);
  buildRef.current = build;

  /* Sent to the file builder, includes any dynamic content we want to add to the file */
  const options = React.useMemo((): FileOptions => {
    const demoSettings = activeDemo ? activeDemo.settings : {};

    return {
      demo: demoSettings,
      keys: props.keys || {},
      merchant: props.merchant,
      server: languages.active.server,
      client: languages.active.client,
      platform: languages.active.platform,
      reader: languages.active.reader,
      country: languages.active.country,
      activePrice,
      // Not loading the DEFAULT_PRICE here because when you switch
      // languages it won't reload the state. Moving DEFAULT_PRICE
      // to language files themselves.
      formattedActivePriceAmount: activePrice
        ? formatPriceAmount(activePrice)
        : '',
      taxActivationStatus,
      checkoutMode: activePrice?.recurring ? 'subscription' : 'payment',
    };
  }, [
    props.keys,
    props.merchant,
    languages,
    activePrice,
    formatPriceAmount,
    activeDemo,
    taxActivationStatus,
  ]);

  // Runs when the page location changes to a new IB type
  useEffect(() => {
    const updatedLanguages = getDefaultLanguage(
      props.languages,
      languagePrefs,
      props.siteCountry,
    );
    const updatedDemo = getActiveDemo({
      demos: props.demos,
      languages: defaultLanguages,
    });

    setDemos(props.demos);
    setActiveDemo(updatedDemo);
    setActiveStep(null);
    setLanguages(updatedLanguages);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, [props.languages]);

  // Rebuild the content of the IB when the language options are updated
  useEffect(() => {
    buildRef.current({languages, options});
  }, [languages, options]);

  const fireEvent = (action: string, data: {[key: string]: string}) => {
    const {platform, client, server, country, reader} = languages
      ? languages.active
      : {};
    trackAction(`integration_builder_${action}`, {
      ...data,
      platform_lang: platform || 'none',
      client_lang: client || 'none',
      server_lang: server || 'none',
      integration_country: country || 'none',
      reader: reader || 'none',
    });
  };

  /* Sets the active step but does not scroll to it */
  const setActiveStepById = (stepId: string) => {
    const step = getStep(stepId);
    setActiveStep(step);
    if (step) {
      setActiveFile(step.file || activeFile);
    }
  };

  const handleScroll = () => {
    // Don't scroll if the user is exploring the code
    if (document.querySelector('.Guide-rightcol:hover')) {
      return;
    }
    const newScrollTop =
      props.containerRef?.current.scrollTop || window.pageYOffset;

    // Track whether we are scrolling up or down
    const scrollUp = prevScrollTop > newScrollTop;
    setPrevScrollTop(newScrollTop);

    // Find the step that's within the first 25% of the screen
    const findStep = (list) => {
      const offset = 120;
      const item = list.find((el) => {
        return el.getBoundingClientRect().top > offset;
      });

      return item;
    };

    const list = Array.from(document.getElementsByClassName('Step--visible'));
    const el = findStep(list);

    if (!el) {
      return;
    }

    // Change the active step only if we have scrolled to a new step
    if (activeStep && el.dataset.id === activeStep.id) {
      return;
    }

    const scrolledStepIsAboveActiveStep =
      !scrollUp && activeStep && activeStep.index > Number(el.dataset.index);
    const scrollStepIsBelowActiveStep =
      scrollUp && activeStep && activeStep.index < Number(el.dataset.index);

    if (scrolledStepIsAboveActiveStep || scrollStepIsBelowActiveStep) {
      // A user can click on a step to activate it without scrolling to it
      // In that case we only want to "resume" activate on scroll for
      // steps that are in the logical scroll order
      // Skip over any step that may be between
      return;
    }

    fireEvent('scroll_step', {
      step_id: el.dataset.id,
    });

    setActiveStepById(el.dataset.id);
  };

  useEffect(() => {
    const el = props.containerRef?.current || window;
    el.addEventListener('scroll', handleScroll, {passive: true});
    return function cleanup() {
      el.removeEventListener('scroll', handleScroll);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, [steps, activeStep]);

  /* Used to switch files in <FileHeader /> */
  const setFile = (selectedFile: string) => () => {
    const eventData = {};
    eventData.filename = selectedFile;

    if (activeStep) {
      eventData.active_step = activeStep.id;
    }

    fireEvent('click_file', eventData);
    setActiveFile(selectedFile);
    setActiveStep(null);
    const codeEl = document.querySelector('.code-view pre');
    if (codeEl) {
      codeEl.scrollTop = 0;
    }
  };

  /* Used to switch between tabs in a Step */
  const setTab = (step: Step, tabId: string, file: string) => {
    const newActiveStep = updateStep(step, tabId, file);
    setActiveStep(newActiveStep);
  };

  /* Sets the active step and scrolls to it */
  const scrollToStep = (stepId: string) => {
    if (stepId === (activeStep && activeStep.id)) {
      return;
    }
    const step = getStep(stepId);
    if (step) {
      const stepRef = document.querySelector(`.Step[data-id="${step.id}"]`);
      const top = stepRef && stepRef.offsetTop;
      if (top) {
        const el = props.containerRef?.current || window;
        el.scrollTo({
          top: top - 20,
          behavior: 'smooth',
        });
      }
    }
  };

  /* Rebuilds the files and steps when a user selects new language settings */
  const changeSetting = (updates: ActiveLanguages) => {
    const newLanguages = {
      ...languagePrefs,
      ...updates,
    };
    const languageDefaults = getDefaultLanguage(
      props.languages,
      newLanguages,
      props.siteCountry,
    );

    const updatedLanguages = {
      ...languageDefaults,
      active: {
        ...languageDefaults.active,
        ...updates,
      },
    };

    const {client, server, platform, reader, country} = updatedLanguages.active;

    setPrefs({
      'integration-country': country,
      reader,
      platform,
      client,
      server,
      lang: server,
    });
    // TODO(mfix) we could replace this w/ the AdvancedPageSelector logic if we correctly set pagePrefs in IBs
    const queryParamChanges = Object.keys(updatedLanguages.active).reduce(
      (hash, name) => {
        if (updates[name] || location.query[name]) {
          hash[name] = updatedLanguages.active[name];
        }
        return hash;
      },
      {},
    );
    replaceLocation({
      ...location,
      query: {...location.query, ...queryParamChanges},
    });

    const newActiveDemo = getActiveDemo({demos, languages: updatedLanguages});

    setLanguages(updatedLanguages);
    setActiveFile('');
    setActiveStep(null);
    setActiveDemo(newActiveDemo);
  };

  /* Fired when a user toggles an extension step */
  /* Rebuilds files and steps */
  const toggleExtension = (id: string) => {
    const {client, server} = languages.active;

    const extensions = updateExtension({
      id,
      options: {
        ...options,
      },
      client,
      server,
    });

    fireEvent('toggle_extension', {
      extension_id: id,
      on: String(!extensions[id]),
    });

    // If we're toggling off an extension, clear the
    // active step
    const updatedActiveStep = !extensions[id] ? activeStep : null;

    setActiveStep(updatedActiveStep);
  };

  /* Adjusts values in the demo, rebuilds files and steps */
  const updateDemo = (updatedDemo: Demo) => {
    const newDemos = {
      ...demos,
      [languages.active.platform]: updatedDemo,
    };

    let newOptions = options;
    if (updatedDemo) {
      newOptions = {
        ...newOptions,
        demo: updatedDemo.settings,
      };
    }

    const {client, server} = languages.active;

    const fileData = getIntegrationFiles({client, server});

    buildFiles({fileData, options: newOptions});
    setDemos(newDemos);
    setActiveDemo(updatedDemo);
  };

  /* Copies a value to the clipboard */
  /* Yes, it's 2020 and this is the most stable way to copy to clipboard */
  const copyValue = (value: string) => {
    const textarea = document.createElement('textarea');
    textarea.style.setProperty('position', 'absolute');
    textarea.style.setProperty('left', '-1000px');
    textarea.style.setProperty('top', '-1000px');
    textarea.value = value;
    let result = false;

    const body = document.body;
    if (body) {
      body.appendChild(textarea);
      textarea.select();
      result = document.execCommand('copy');
      body.removeChild(textarea);
    }

    return result;
  };

  return (
    <GuideContext.Provider
      value={{
        languages,
        merchant: props.merchant,
        keys: props.keys,
        activeStep,
        activeFile,
        activeDemo,
        options,
        setFile,
        changeSetting,
        scrollToStep,
        setActiveStepById,
        copyValue,
        toggleExtension,
        updateDemo,
        setTab,
        fireEvent,
      }}
    >
      {props.children}
    </GuideContext.Provider>
  );
};

export const useGuide = () => {
  return useContext(GuideContext);
};

export const GuideConsumer = GuideContext.Consumer;
